import { useState } from "react";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/consultations`, { headers: { "X-Admin-Key": key } });
      setItems(res.data);
    } catch {
      setError("Invalid admin key.");
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-page" className="pt-36 md:pt-44 pb-24 min-h-screen">
      <SEO title="Admin — Consultation Requests | Hive Interiors" description="Admin area." />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary">Admin</p>
        <h1 className="mt-4 font-heading text-4xl md:text-5xl tracking-tight">Consultation Requests</h1>

        {items === null && (
          <form onSubmit={load} data-testid="admin-key-form" className="mt-10 flex gap-3 max-w-md">
            <div className="relative flex-1">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                className="w-full bg-transparent border border-border pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-primary transition-colors duration-300"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-load-button"
              disabled={loading}
              className="bg-primary text-primary-foreground px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-accent hover:text-accent-foreground transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "View"}
            </button>
          </form>
        )}
        {error && <p data-testid="admin-error" className="mt-4 text-sm text-red-400">{error}</p>}

        {items !== null && (
          <div className="mt-10 space-y-4" data-testid="admin-requests-list">
            <p className="text-sm text-muted-foreground">{items.length} request(s)</p>
            {items.map((r) => (
              <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-card border border-border p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="font-heading text-xl">{r.name}</p>
                  <p className="text-sm text-muted-foreground">{r.phone} · {r.email}</p>
                  <p className="text-sm text-muted-foreground">{r.city || "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary">{r.project_type}</p>
                  <p className="text-sm text-muted-foreground mt-1">Budget: {r.budget || "Not specified"}</p>
                  <p className="text-sm text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/85 leading-relaxed">{r.message || "—"}</p>
                  {r.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {r.photos.map((p) => (
                        <a key={p} href={`${API}/uploads/${p}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${API}/uploads/${p}`} alt="Project upload" className="w-16 h-16 object-cover border border-border" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-muted-foreground">No consultation requests yet.</p>}
          </div>
        )}
      </div>
    </main>
  );
}
